import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useUserData } from './UserDataContext';
import { Coins, Ticket, Tag, Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import goldIcon from '../assets/gold icon.png';
import { toast } from 'sonner';
import { TAGS, TOPIC_TAGS } from '../constants/TagDictionary';
import { ForumService } from '../services/api';
import { ImageCropperModal } from './ImageCropperModal';

interface CreateForumModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateForumModal({ open, onOpenChange }: CreateForumModalProps) {
    const { userProfile, createForum } = useUserData();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [rules, setRules] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImage, setCropImage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [agreed, setAgreed] = useState(false);

    React.useEffect(() => { if (!open) setAgreed(false); }, [open]);

    // Caps
    const ownedCount = (userProfile.ownedForumIds || []).length;
    const maxForums = 5;
    const isCapReached = ownedCount >= maxForums;

    // Costs
    const credits = userProfile.forumCreationCredits || 0;
    const hasCredits = credits > 0;
    const goldCost = 500;
    const hasGold = (userProfile.goldBalance || 0) >= goldCost;

    const canCreate = !isCapReached && (hasCredits || hasGold);

    const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Image size must be less than 5MB");
                return;
            }
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setCropImage(reader.result as string);
                setCropModalOpen(true);
            };
            reader.readAsDataURL(file);
            e.target.value = '';
        }
    };

    const dataURLToBlob = (dataURL: string) => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    };

    const handleCropSave = (croppedImageUrl: string) => {
        setBannerPreview(croppedImageUrl);
        const blob = dataURLToBlob(croppedImageUrl);
        const file = new File([blob], "forum-banner.jpg", { type: "image/jpeg" });
        setBannerFile(file);
        setCropModalOpen(false);
    };

    const handleSubmit = async () => {
        if (!title.trim() || !description.trim()) {
            toast.error("Title and description are required");
            return;
        }

        setLoading(true);
        try {
            let bannerUrl = ''; // Default to empty to use gradient placeholder

            if (bannerFile && userProfile.id) {
                try {
                    bannerUrl = await ForumService.uploadCommunityBanner(String(userProfile.id), 'forum', bannerFile);
                } catch (uploadError) {
                    console.error("Banner upload failed, using default:", uploadError);
                    toast.error("Banner upload failed, using default image");
                }
            }

            const forumId = await createForum({
                name: title,
                description,
                rules,
                tags,
                bannerUrl: bannerUrl,
                bannerImage: bannerUrl
            });
            onOpenChange(false);

            // Navigate to new forum
            const newForum = {
                id: forumId,
                title: title,
                description: description,
                image: bannerUrl,
                posts: 0,
                ownerId: userProfile?.id,
                lastOwnershipTransferAt: null
            };

            // Refresh communities list
            window.dispatchEvent(new CustomEvent('REFRESH_COMMUNITIES'));

            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('navigateToHistoryForum', { detail: newForum }));
            }, 500);

            // Reset form
            setTitle('');
            setDescription('');
            setRules('');
            setTags([]);
            setBannerFile(null);
            setBannerPreview(null);
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || "Failed to create forum");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* Width is inline, not a class.

                w-[min(480px,92vw)] was here and had no rule in index.css, which
                is committed pre-compiled Tailwind — so it applied no width at
                all. cn() is twMerge, so it still won the conflict and stripped
                the wrapper's own w-[92%] (which has no rule either), while
                max-w-none replaced max-w-lg. The dialog was left with no width
                and no cap, and a fixed-position grid with neither shrinks to fit
                its content. That is why it looked too narrow.

                max-w-none stays: it is doing real work, overriding the wrapper's
                max-w-lg at 512px, which would otherwise cap this below 560. */}
            <DialogContent
                style={{ width: 'min(560px, 92vw)' }}
                className="max-w-none bg-light-pattern dark:bg-[#1a1a1a] border-black/5 dark:border-white/10 text-black dark:text-white flex flex-col h-[80vh] max-h-[80vh] p-0 overflow-hidden shadow-2xl gap-0"
            >

                {/* ── Step 0: Agreement screen ───────────────────────────────── */}
                {!agreed && (
                    <div className="flex flex-col h-full p-6 gap-5 overflow-y-auto">
                        <div>
                            <h2 className="text-2xl font-bold text-black dark:text-white mb-1">What is a Forum?</h2>
                            <p className="text-sm text-black/50 dark:text-white/50">Please read before creating one.</p>
                        </div>

                        <div className="flex flex-col gap-4 text-sm text-black/80 dark:text-white/80 leading-relaxed">
                            <p>
                                A <strong>Forum</strong> is a space for deeper intellectual exploration of specific subjects — not a casual groupchat. Forums are built for thoughtful, substantive conversations that go beyond surface-level discussion.
                            </p>
                            <p>
                                Forums are rooted in <strong>History, Culture, and Tradition</strong>. They are the right place for topics like African history, diaspora identity, indigenous traditions, language, philosophy, and civilisational discourse.
                            </p>
                            <div className="rounded-xl p-4 border border-blue-400/30 bg-blue-50 dark:bg-blue-950/30">
                                <p className="font-semibold text-blue-700 dark:text-blue-400 mb-1">Forums are not groupchats</p>
                                <p className="text-blue-800 dark:text-blue-300">
                                    If you want a community to casually hang out and discuss shared interests, create a <strong>Tribe</strong> instead. Forums are for focused, knowledge-driven dialogue.
                                </p>
                            </div>
                        </div>

                        <div className="mt-auto">
                            <Button
                                className="w-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white font-bold rounded-xl h-12"
                                onClick={() => setAgreed(true)}
                            >
                                I understand — create a Forum
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── Step 1+: Creation form ─────────────────────────────────── */}
                {agreed && <>
                <DialogHeader className="p-6 pb-4 border-b border-black/10 dark:border-white/10 shrink-0">
                    <DialogTitle className="text-xl font-bold flex items-center justify-between">
                        <span>Create Forum</span>
                        <div className="text-xs font-normal px-2 py-1 rounded bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/60">
                            Owned: {ownedCount}/{maxForums}
                        </div>
                    </DialogTitle>
                    <DialogDescription className="text-sm text-black/60 dark:text-white/60">
                        Forums are public spaces for niche discussion about culture, history and tradition.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 custom-scrollbar min-h-0 bg-transparent">
                    <div className="py-6 space-y-6">
                        {/* Banner Upload Section */}
                        <div className="space-y-2">
                            <Label className="flex justify-between items-center font-outfit">
                                <span className="font-bold text-base">Forum Banner</span>
                                <span className="text-[10px] text-black/40 dark:text-white/40 uppercase font-bold tracking-wider">Recommended: 1200x300</span>
                            </Label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleBannerChange}
                            />
                            <div 
                                onClick={() => fileInputRef.current?.click()}
                                className="relative group cursor-pointer"
                            >
                                <div className={`w-full h-32 rounded-2xl overflow-hidden border-2 border-dashed transition-all flex items-center justify-center bg-black/5 dark:bg-white/5 ${bannerPreview ? 'border-amber-500/50 dark:border-blue-500/50' : 'border-black/10 dark:border-white/10 hover:border-amber-500/30'}`}>
                                    {bannerPreview ? (
                                        <>
                                            <img src={bannerPreview} className="w-full h-full object-cover shadow-inner" alt="Banner preview" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <div className="flex flex-col items-center gap-2">
                                                    <Upload className="w-10 h-10 text-white" />
                                                    <span className="text-white text-xs font-bold font-outfit">Change Image</span>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setBannerFile(null);
                                                    setBannerPreview(null);
                                                }}
                                                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-red-500 transition-all transform hover:scale-110 shadow-lg"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-center gap-3 text-black/40 dark:text-white/40 group-hover:text-amber-500/60 transition-colors font-outfit">
                                            <div className="p-4 rounded-full bg-black/5 dark:bg-white/5 shadow-sm">
                                                <ImageIcon className="w-10 h-10" />
                                            </div>
                                            <div className="text-center">
                                                <p className="text-base font-bold">Upload Custom Banner</p>
                                                <p className="text-xs">Tap to browse files</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {loading && bannerFile && (
                                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-10 rounded-xl">
                                            <Loader2 className="w-10 h-10 text-white animate-spin" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label className="font-bold flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                                    Title
                                </Label>
                                <Input
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g. Medieval Saharan Empires"
                                    maxLength={50}
                                    className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 h-12 text-base font-medium rounded-xl"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-bold flex items-center gap-2">
                                    <ImageIcon className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                                    Display Name
                                </Label>
                                <Input
                                    value={title}
                                    disabled
                                    placeholder="Auto-generated"
                                    className="bg-zinc-200/30 dark:bg-black/20 border-black/10 dark:border-white/10 h-12 text-base font-medium rounded-xl opacity-60"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="font-bold">Description</Label>
                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="What is this forum about?"
                                maxLength={200}
                                className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 min-h-[100px] text-base font-medium rounded-xl p-4"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="font-bold">Rules (Optional)</Label>
                            <Textarea
                                value={rules}
                                onChange={(e) => setRules(e.target.value)}
                                placeholder="Be respectful, No spam..."
                                maxLength={500}
                                className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 min-h-[100px] text-base font-medium rounded-xl p-4"
                            />
                        </div>

                        <div className="space-y-3">
                            <Label className="font-bold flex items-center justify-between">
                                <span>Tags (Max 5)</span>
                                <span className="text-[10px] text-[#F39C12] dark:text-[#4478ea] font-black uppercase">{tags.length}/5 Selected</span>
                            </Label>
                            <div className="flex flex-wrap gap-2 p-3 rounded-2xl bg-zinc-100/50 dark:bg-black/40 border border-black/10 dark:border-white/10">
                                {TOPIC_TAGS.map((tag) => (
                                    <button
                                        key={tag}
                                        type="button"
                                        onClick={() => {
                                            if (tags.includes(tag)) {
                                                setTags(tags.filter(t => t !== tag));
                                            } else {
                                                if (tags.length >= 5) {
                                                    toast.error("Max 5 tags allowed");
                                                    return;
                                                }
                                                setTags([...tags, tag]);
                                            }
                                        }}
                                        className={`px-4 py-2 text-xs font-bold rounded-full border transition-all duration-300 ${tags.includes(tag)
                                            ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#3498DB] text-white border-transparent shadow-md transform scale-105'
                                            : 'bg-white dark:bg-[#1a1a1a] text-black/60 dark:text-white/60 border-black/10 dark:border-white/10 hover:border-[#F39C12] dark:hover:border-[#4478ea]'
                                            }`}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Cost Chip */}
                        <div className="flex items-center gap-4 p-4 rounded-2xl bg-linear-to-r from-amber-50 to-orange-50 dark:from-amber-900/5 dark:to-orange-900/5 border border-amber-200/50 dark:border-amber-800/30">
                            <div className="p-3 rounded-full bg-white dark:bg-black/40 shadow-sm">
                                <img src={goldIcon as any} alt="Gold" className="h-8 w-auto object-contain" />
                            </div>
                            <div className="flex-1">
                                {isCapReached ? (
                                    <>
                                        <p className="text-sm font-bold text-red-500 uppercase tracking-tight">Forum Cap Reached</p>
                                        <p className="text-xs text-red-400 font-medium leading-tight">You have reached the maximum Forum limit (5).</p>
                                    </>
                                ) : hasCredits ? (
                                    <>
                                        <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-tight">Free Creation</p>
                                        <p className="text-xs text-emerald-500 font-medium leading-tight">Using 1 Forum Creation Credit ({credits} remaining)</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm font-bold text-amber-600 dark:text-blue-400 uppercase tracking-tight">Creation Cost</p>
                                        <div className="flex items-center gap-1.5 font-black text-lg text-amber-700 dark:text-blue-300">
                                            <span>{goldCost} Gold</span>
                                            {!hasGold && <span className="text-xs text-red-500 font-bold ml-2">(Insufficient Funds)</span>}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter className="p-6 pt-4 border-t border-black/10 dark:border-white/10 shrink-0 pb-8 sm:pb-6">
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || !canCreate}
                        className="w-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#3498DB] hover:opacity-90 text-white py-6 rounded-xl text-lg font-bold shadow-lg shadow-orange-500/20 dark:shadow-blue-500/20"
                    >
                        {loading ? 'Creating...' : 'Create Forum'}
                    </Button>
                </DialogFooter>
                </>}
            </DialogContent>

            <ImageCropperModal
                isOpen={cropModalOpen}
                onClose={() => setCropModalOpen(false)}
                imageUrl={cropImage || ''}
                onSave={handleCropSave}
                aspectRatio={4}
                outputWidth={1600}
                title="Adjust Forum Banner"
                description="Fit the image into the 1200x300 frame"
                circular={false}
                onUploadNew={() => {
                    setCropModalOpen(false);
                    setTimeout(() => fileInputRef.current?.click(), 100);
                }}
            />
        </Dialog>
    );
}
